import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useUser } from "@/store/user";

// Group lessons by skill path
const SKILL_PATHS = [
  { key: "imagery",   name: "الصور البلاغية",   icon: "🖼️", skills: ["البلاغة والتصوير"] },
  { key: "rhythm",    name: "الموسيقى اللغوية", icon: "🎵", skills: ["الموسيقى اللغوية"] },
  { key: "style",     name: "الأسلوب والتأثير",  icon: "✒️", skills: ["تكثيف المعنى", "الأسلوب العاطفي", "البلاغة والتأثير"] },
  { key: "rhetoric",  name: "البلاغة التطبيقية", icon: "📜", skills: ["البلاغة التطبيقية", "المقارنة وبناء الحجّة", "الحوار والتفكير"] },
  { key: "speech",    name: "الخطابة والإقناع",  icon: "🎤", skills: ["التحدّث والإقناع"] },
];

function StatBox({ value, label, color = "var(--gold)" }) {
  return (
    <div className="stat-tile">
      <div className="v" style={{ color }}>{value}</div>
      <div className="l">{label}</div>
    </div>
  );
}

export default function CurriculumPage() {
  const navigate = useNavigate();
  const { progress } = useUser();
  const [lessons, setLessons] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.listLessons(), api.listMilestones()])
      .then(([ls, ms]) => { setLessons(ls); setMilestones(ms); })
      .finally(() => setLoading(false));
  }, []);

  const completed = useMemo(
    () => new Set(progress.completed_lessons || []),
    [progress.completed_lessons]
  );

  const overall = lessons.length
    ? Math.round((completed.size / lessons.length) * 100)
    : 0;

  // Group lessons by skill path
  const grouped = useMemo(() => {
    const out = SKILL_PATHS.map((p) => ({
      ...p,
      lessons: lessons.filter((l) => p.skills.includes(l.skill)),
    }));
    // Catch-all for any skill not matched
    const matchedIds = new Set(out.flatMap((p) => p.lessons.map((l) => l.id)));
    const orphans = lessons.filter((l) => !matchedIds.has(l.id));
    if (orphans.length) {
      out.push({ key: "other", name: "دروس أخرى", icon: "📘", lessons: orphans });
    }
    return out.filter((p) => p.lessons.length > 0);
  }, [lessons]);

  const totalXp = progress.xp || 0;
  const earnedBadges = progress.badges?.length || 0;
  const totalBadges = lessons.length;

  // Estimate time remaining: sum durations of incomplete lessons
  const timeRemaining = useMemo(() => {
    let mins = 0;
    lessons.forEach((l) => {
      if (completed.has(l.id)) return;
      const m = (l.duration || "").match(/(\d+)/);
      if (m) mins += parseInt(m[1], 10);
    });
    return mins;
  }, [lessons, completed]);

  return (
    <div className="shell has-bottom-nav" data-testid="curriculum-page">
      <div className="page-header">
        <h1>
          منهج <span className="gold">بَلاغة</span>
        </h1>
        <p>تابع تقدّمك في رحلة تعلّم البلاغة العربيّة. كلّ درس يبنيك خطوة، وكلّ شارة تُتوّج إنجازك.</p>
      </div>

      {/* Overall progress hero */}
      <div className="curriculum-hero" data-testid="curriculum-overall">
        <div className="curriculum-ring" style={{ "--p": overall }}>
          <div className="curriculum-ring-inner">
            <div className="curriculum-ring-pct">{overall}%</div>
            <div className="curriculum-ring-label">إكمال المنهج</div>
          </div>
        </div>
        <div className="curriculum-summary">
          <h2 style={{ margin: "0 0 4px", fontFamily: "'Reem Kufi', sans-serif" }}>
            {overall === 100 ? "أتممت المنهج كاملًا 🎉" : `أكملت ${completed.size} من ${lessons.length} دروس`}
          </h2>
          <p className="dim" style={{ lineHeight: 1.85 }}>
            {overall === 0 && "ابدأ من الدرس الأوّل واستكشف عشرات الأمثلة والأنشطة."}
            {overall > 0 && overall < 100 && `يتبقّى ${lessons.length - completed.size} درسًا · حوالي ${timeRemaining} دقيقة من التعلّم.`}
            {overall === 100 && "أنت الآن سفير للبلاغة العربيّة! استكشف التحدّيات لتطوّر مهاراتك."}
          </p>
          <div className="row" style={{ marginTop: 14 }}>
            <button
              data-testid="curriculum-continue-btn"
              className="btn btn-primary"
              onClick={() => {
                const next = lessons.find((l) => !completed.has(l.id)) || lessons[0];
                if (next) navigate(`/lesson/${next.id}`);
              }}
            >
              {overall > 0 && overall < 100 ? "تابع التعلّم ←" : "ابدأ الآن ←"}
            </button>
          </div>
        </div>
      </div>

      {/* Milestones */}
      {milestones.length > 0 && (
        <>
          <h2 className="section-title">شارات الإنجاز</h2>
          <div className="milestones-grid" data-testid="milestones-grid">
            {milestones.map((m) => (
              <div
                key={m.id}
                className={`milestone-card ${m.earned ? "earned" : ""}`}
                data-testid={`milestone-${m.id}`}
              >
                <div className="ms-icon">{m.earned ? m.icon : "🔒"}</div>
                <div className="ms-info">
                  <div className="ms-name">{m.name}</div>
                  <div className="ms-desc">{m.desc}</div>
                  <div className="progress-track" style={{ height: 5, marginTop: 6 }}>
                    <div className="progress-fill" style={{ width: `${(m.progress / m.total) * 100}%` }} />
                  </div>
                  <div className="ms-progress muted">{m.progress}/{m.total}</div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Stats */}
      <div className="stat-grid" style={{ marginTop: 18 }} data-testid="curriculum-stats">
        <StatBox value={completed.size} label="📚 دروس مكتملة" />
        <StatBox value={`${earnedBadges}/${totalBadges}`} label="🏅 شارات" />
        <StatBox value={totalXp} label="⭐ نقاط الخبرة" />
      </div>

      {/* Skill paths */}
      {loading ? (
        <div className="muted" style={{ marginTop: 22 }}>يُحمَّل المنهج...</div>
      ) : (
        <>
          <h2 className="section-title">مسارات المهارات</h2>
          <div className="paths-grid" data-testid="paths-grid">
            {grouped.map((path) => {
              const total = path.lessons.length;
              const done = path.lessons.filter((l) => completed.has(l.id)).length;
              const pct = total ? Math.round((done / total) * 100) : 0;
              return (
                <div key={path.key} className="path-card" data-testid={`path-${path.key}`}>
                  <div className="row" style={{ justifyContent: "space-between", marginBottom: 10 }}>
                    <div className="row" style={{ gap: 10 }}>
                      <div className="path-icon">{path.icon}</div>
                      <div>
                        <h3 style={{ margin: 0, fontSize: 17, fontFamily: "'Reem Kufi', sans-serif" }}>
                          {path.name}
                        </h3>
                        <div className="muted" style={{ fontSize: 12.5 }}>
                          {done}/{total} درس · {pct}%
                        </div>
                      </div>
                    </div>
                    {pct === 100 && <span className="badge-pill" style={{ fontSize: 12 }}>✓ مكتمل</span>}
                  </div>
                  <div className="progress-track" style={{ height: 7 }}>
                    <div className="progress-fill" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="path-lessons" style={{ marginTop: 12 }}>
                    {path.lessons.map((l) => {
                      const isDone = l.completed;
                      const isLocked = l.locked;
                      return (
                        <button
                          key={l.id}
                          className={`path-lesson-item ${isDone ? "done" : ""} ${isLocked ? "locked" : ""}`}
                          onClick={() => {
                            if (isLocked) return;
                            navigate(`/lesson/${l.id}`);
                          }}
                          data-testid={`path-lesson-${l.id}`}
                          disabled={isLocked}
                        >
                          <span className="pl-status">{isDone ? "✓" : isLocked ? "🔒" : l.id}</span>
                          <span className="pl-title">{l.title}</span>
                          <span className="pl-meta muted">{l.duration}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* All lessons quick list */}
          <h2 className="section-title">كلّ الدروس</h2>
          <div className="curriculum-table" data-testid="curriculum-table">
            {lessons.map((l) => {
              const isDone = l.completed;
              const isLocked = l.locked;
              const status = isDone ? "done" : isLocked ? "locked" : "available";
              const statusLabel = isDone ? "✓ مكتمل" : isLocked ? "🔒 مغلق" : "قيد الدراسة";
              return (
                <button
                  key={l.id}
                  className={`curr-row ${status}`}
                  onClick={() => {
                    if (isLocked) return;
                    navigate(`/lesson/${l.id}`);
                  }}
                  data-testid={`curr-row-${l.id}`}
                  disabled={isLocked}
                >
                  <span className="curr-num">{l.id}</span>
                  <div className="curr-info">
                    <div className="curr-title">{l.title}</div>
                    <div className="curr-meta muted">
                      ⏱ {l.duration} · {l.skill} · +{l.xp_total} XP
                    </div>
                  </div>
                  <span className={`curr-status ${status}`}>
                    {statusLabel}
                  </span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
